import { z } from "zod";
import { billingTransferRequestSchema } from "@common/dto/billing/transfer-request.dto";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { utcDatetime } from "@common/helpers/date";
import { OrganizationDtoSchema } from "@common/schemas/organization.schema";
import { userResponseSchema } from "@common/schemas/user.schema";
import { OrganizationServicesOffered } from "@common/types/organization";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import { entityWithUnloadedRelationsResponseSchema } from "@common/schemas/domains/entity/entity-response.schema";

const workspaceUserAssignmentResponseSchema = z.object({
    userId: z.number(),
    workspaceId: z.string().uuid(),
    role: z.nativeEnum(UserWorkspaceRole),
    organizationId: z.string().nullable(),
});

const workspaceResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    primaryAdminUserId: z.number().nullable(),
    userAssignments: z.array(workspaceUserAssignmentResponseSchema),
    isDemo: z.boolean(),
    isFirmWorkspace: z.boolean(),
    createdAt: utcDatetime,
    onboardingCompletedAt: utcDatetime.nullable(),
    glFirstEnabledAt: utcDatetime.nullable(),
    glFirstEnabled: z.boolean(),
    demoDataSeedingStartedAt: utcDatetime.nullable(),
    demoDataSeedingFinishedAt: utcDatetime.nullable(),
    iconColor: z.string().nullable(),
    plan: z.nativeEnum(FLAT_RATE_PLANS).nullable(),
    planBeforeTransfer: z.nativeEnum(FLAT_RATE_PLANS).nullable(),
    bookkeepingStartDate: z.string().date(),
    bookkeepingEndDate: z.string().date().nullable(),
    freeUsageLimit: z.number(),
    tempMigratedToNormalizedDescriptions: z.boolean(),
    tempMigratedToReasoningCategorization: z.boolean(),
    tempMigratedToSimilarityRules: z.boolean(),
    isFreshbooksCustomer: z.boolean(),
    showAccountNumbers: z.boolean(),
    isDateOnlyMigrated: z.boolean(),
    timezone: z.nativeEnum(WorkspaceTimezone),
});

const workspaceAssignmentResponseSchema = z.object({
    user: userResponseSchema,
    role: z.nativeEnum(UserWorkspaceRole),
    organizationId: z.string().nullable(),
});

const organizationWorkspaceAssignmentResponseSchema = z.object({
    workspaceId: z.string().uuid(),
    organizationId: z.string(),
    organization: OrganizationDtoSchema.optional(),
    billingOwnership: z.boolean(),
    servicesOffered: z.array(z.nativeEnum(OrganizationServicesOffered)),
    note: z.string().nullable(),
    createdAt: utcDatetime,
    updatedAt: utcDatetime,
});

const workspacePendingInviteResponseSchema = z.object({
    id: z.number(),
    email: z.string(),
    role: z.nativeEnum(UserWorkspaceRole),
    iconColor: z.string(),
    entityAssignments: z.array(z.object({ entityId: z.number() })).optional(),
});

export const workspaceDetailsResponseSchema = z.object({
    workspace: workspaceResponseSchema,
    assignments: z.array(workspaceAssignmentResponseSchema),
    pendingInvites: z.array(workspacePendingInviteResponseSchema),
    organizationAssignments: z.array(
        organizationWorkspaceAssignmentResponseSchema,
    ),
    pendingTransferRequests: z.array(billingTransferRequestSchema),
});

export const workspacesDetailsPageResponseSchema = z.object({
    data: z.array(
        workspaceDetailsResponseSchema.extend({
            workspace: workspaceResponseSchema.extend({
                entities: z.array(entityWithUnloadedRelationsResponseSchema),
            }),
        }),
    ),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});

export type WorkspacesDetailsPageItemInput = z.input<
    typeof workspacesDetailsPageResponseSchema
>["data"][number];
